import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DeleteProfileAction } from '@/components/common/DeleteProfileAction';
import { ProfileCardGrid, ProfileGridItem } from '@/components/common/ProfileCardGrid';
import { ProfileLayout } from '@/components/common/ProfileLayout';
import { ProfileSetupPrompt } from '@/components/common/ProfileSetupPrompt';
import { SectionEditButton } from '@/components/common/SectionEditButton';
import type { PartnerProfileData } from '../../api/partner-profile.api';
import { fetchActivePartnerCategories } from './partner-categories.api';
import { useDeletePartnerProfile } from './usePartnerProfileMutations';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';
import { PartnerProfileHero } from './PartnerProfileHero';
import {
  PartnerAboutCard,
  PartnerContactCard,
  PartnerOnlineCard,
  PartnerServicesCard,
} from './PartnerCards';
import { PartnerSectionEditor } from './PartnerSectionEditor';
import {
  PARTNER_SECTIONS,
  type PartnerEditorTarget,
  type PartnerSectionId,
} from './partner-sections';

interface PartnerProfileSectionsProps {
  data: PartnerProfileData;
  readOnly: boolean;
  entityId: string;
}

interface PartnerCardsProps {
  data: PartnerProfileData;
  readOnly: boolean;
  entityId: string;
  onOpen: (section: PartnerEditorTarget) => void;
  categoryNameById: Record<string, string>;
}

function editButton(
  readOnly: boolean,
  section: PartnerSectionId,
  onOpen: (s: PartnerEditorTarget) => void,
  /** Names the button after the card it sits on when that differs from the section it opens. */
  label?: string,
) {
  if (readOnly) return undefined;
  return (
    <SectionEditButton
      section={label ?? PARTNER_SECTIONS[section].title}
      onClick={() => onOpen(section)}
    />
  );
}

function PartnerDeleteAction({ entityId }: { entityId: string }) {
  const remove = useDeletePartnerProfile(entityId);
  return (
    <DeleteProfileAction
      label={S.DELETE_PROFILE}
      confirmTitle={S.DELETE_CONFIRM_TITLE}
      confirmBody={S.DELETE_CONFIRM_BODY}
      confirmLabel={S.DELETE_CONFIRM_ACTION}
      isDeleting={remove.isPending}
      onDelete={() => remove.mutate()}
      align="start"
    />
  );
}

/**
 * The cards, laid out as in the approved mockup: About beside Contact button,
 * Services beside Online presence (which carries Delete Profile at its foot).
 * About and Online presence are a little wider than the mockup's 7/5 split so
 * real partner content (longer bios, links) fits (client feedback 2026-10-15).
 */
function PartnerCards({ data, readOnly, entityId, onOpen, categoryNameById }: PartnerCardsProps) {
  const card = (section: PartnerSectionId, label?: string) => ({
    data,
    categoryNameById,
    action: editButton(readOnly, section, onOpen, label),
    onAdd: readOnly ? undefined : () => onOpen(section),
  });
  return (
    <ProfileCardGrid>
      {/* About carries six facts and the bio, so it gets the wider column. */}
      <ProfileGridItem span={8}>
        <PartnerAboutCard {...card('about')} />
      </ProfileGridItem>
      <ProfileGridItem span={4}>
        {/* The contact preference is edited with the About details. */}
        <PartnerContactCard {...card('about', S.CONTACT_BUTTON)} />
      </ProfileGridItem>
      <ProfileGridItem span={6}>
        <PartnerServicesCard {...card('services')} />
      </ProfileGridItem>
      <ProfileGridItem span={6}>
        <PartnerOnlineCard
          {...card('online')}
          footer={readOnly ? undefined : <PartnerDeleteAction entityId={entityId} />}
        />
      </ProfileGridItem>
    </ProfileCardGrid>
  );
}

/**
 * The Trusted Partner profile module's `Sections` component (ADR FE-1
 * registry, FR-28): the cover-photo header plus one card per section, each
 * edited in its own side panel (MYN-217, profile-redesign FR-164 to FR-169,
 * FR-176, FR-177). A profile that does not exist yet shows the one-panel
 * setup prompt instead (FR-181).
 */
export function PartnerProfileSections({ data, readOnly, entityId }: PartnerProfileSectionsProps) {
  const [openSection, setOpenSection] = useState<PartnerEditorTarget | null>(null);
  const isNew = !data.id;
  // Shared with ServicesSection's own identical query - same queryKey, so
  // React Query dedupes to one network request and one cache entry.
  const categoriesQuery = useQuery({
    queryKey: ['partner-categories', 'active'],
    queryFn: fetchActivePartnerCategories,
  });
  const categoryNameById = Object.fromEntries(
    (categoriesQuery.data ?? []).map((c) => [c.id, c.name]),
  );

  return (
    <ProfileLayout>
      <PartnerProfileHero
        data={data}
        entityId={entityId}
        onEditImages={readOnly || isNew ? undefined : () => setOpenSection('images')}
      />
      {isNew ? (
        <ProfileSetupPrompt onStart={readOnly ? undefined : () => setOpenSection('setup')} />
      ) : (
        <PartnerCards
          data={data}
          readOnly={readOnly}
          entityId={entityId}
          onOpen={setOpenSection}
          categoryNameById={categoryNameById}
        />
      )}
      {!readOnly && (
        <PartnerSectionEditor
          data={data}
          entityId={entityId}
          section={openSection}
          onClose={() => setOpenSection(null)}
          categoryNameById={categoryNameById}
        />
      )}
    </ProfileLayout>
  );
}
